import React from 'react';

import { ChartsReferenceLine } from '@mui/x-charts';
import {
    useDrawingArea,
    useSvgRef,
    useXScale,
    useYScale,
} from '@mui/x-charts/hooks';
import { useChartContext } from '@mui/x-charts/context/ChartProvider';

import { AxisId } from 'ff/debrief/models';
import { useChrono, useDebriefApp } from 'ff/debrief/hooks';
import AirplaneProfileIcon from 'ff/debrief/components/customIcons/AirplaneProfileIcon';

interface Props {
    axis: AxisId;
    currentXValue: number;
    currentAltitude: number;
    withAirplane?: boolean;
    onXValueClick?: (xValue: number) => void;
}

const Scrubber = React.memo(function Scrubber(
    props: React.PropsWithChildren<Props>
) {
    const app = useDebriefApp();

    const svgRef = useSvgRef();
    const xScale = useXScale<'linear'>();
    const yScale = useYScale(props.withAirplane ? props.axis : null);
    const chrono = useChrono();
    const drawingArea = useDrawingArea();
    const { instance } = useChartContext();

    const isDragging = React.useRef(false);

    const handleSetTime = (evt: MouseEvent) => {
        const svg = evt.currentTarget as SVGSVGElement;
        const viewportPoint = svg.createSVGPoint(); // Create an SVGPoint

        // Set the point's coordinates to the event's client position
        viewportPoint.x = evt.clientX;
        viewportPoint.y = evt.clientY;

        // Transform the point to the SVG coordinate system
        const svgPoint = viewportPoint.matrixTransform(
            svg.getScreenCTM().inverse()
        );

        // Set xValue within bounds of drawing area
        if (instance.isPointInside(svgPoint.x, svgPoint.y)) {
            const xValue = xScale.invert(svgPoint.x);

            if (props.onXValueClick) {
                props.onXValueClick(xValue);
            } else {
                // Backward compatibility: assume xValue is time
                chrono.setTime(xValue);
                app.scrollSegmentIntoView();
            }
        }
    };

    const handleMouseDown = (evt: MouseEvent) => {
        isDragging.current = true;
        handleSetTime(evt);
    };
    const handleMouseMove = (evt: MouseEvent) => {
        if (!isDragging.current) return;
        handleSetTime(evt);
    };

    const handleMouseUp = () => {
        isDragging.current = false;
    };

    React.useEffect(() => {
        const svgEl = svgRef.current;
        svgEl.addEventListener('mousedown', handleMouseDown);
        svgEl.addEventListener('mousemove', handleMouseMove);
        svgEl.addEventListener('mouseup', handleMouseUp);
        return () => {
            svgEl.removeEventListener('mousedown', handleMouseDown);
            svgEl.removeEventListener('mousemove', handleMouseMove);
            svgEl.removeEventListener('mouseup', handleMouseUp);
        };
    });

    if (
        instance.isPointInside(xScale(props.currentXValue), drawingArea.bottom)
    ) {
        return (
            <>
                <ChartsReferenceLine x={props.currentXValue} />
                {props.withAirplane && (
                    <AirplaneProfileIcon
                        x={xScale(props.currentXValue) - 12}
                        y={yScale(props.currentAltitude) - 15}
                    />
                )}
            </>
        );
    } else {
        return null;
    }
});

export default Scrubber;
